import { createApi } from "@reduxjs/toolkit/query/react";
import baseQuery from "../../app/apiBaseQuery";

export const CommonAPI = createApi({
  reducerPath: "CommonAPI",
  baseQuery,
  tagTypes: ['Notifications', 'CampaignForReviewer', 'Subscription', 'Transactions'],
  endpoints: (builder) => ({
    getNotifications: builder.query({
      query: (params) => ({
        url: "admin/getNotifications",
        method: "POST",
        body: params,
      }),
      providesTags: ['Notifications'],
    }),
    deleteNotification: builder.mutation({
      query: (params) => ({
        url: "admin/deleteNotification",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ['Notifications'],
    }),
    readNotification: builder.mutation({
      query: (params) => ({
        url: "admin/markAsRead",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ['Notifications'],
    }),
    readAllNotification: builder.mutation({
      query: () => ({
        url: "admin/markAllAsRead",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ['Notifications'],
    }),
    getDashboard: builder.query({
      query: () => ({
        url: "admin/getCommonDashboard",
        method: "GET",
      }),
    }),
    updateProfile: builder.mutation({
      query: (formData) => ({
        url: "user/updateUserProfile",
        method: "POST",
        body: formData,
      }),
    }),
    changeProfilePassword: builder.mutation({
      query: (params) => ({
        url: "user/changeProfilePassword",
        method: "POST",
        body: params,
      }),
    }),
    getUserData: builder.query({
      query: () => ({
        url: "user/getUserData",
        method: "GET",
      }),
    }),
    getEmployerPostedJobs: builder.query({
      query: ({ search = "", employmentType = "", page = 1, limit = 10 } = {}) => ({
        url: `user/getEmployerPostedJobs?search=${encodeURIComponent(search)}&employmentType=${employmentType}&page=${page}&limit=${limit}`,
        method: "GET",
      }),
    }),
    getEmployeeAssignedJobs: builder.query({
      query: ({ search = "", page = 1, limit = 10 } = {}) => ({
        url: `user/getEmployeeAssignedJobs?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`,
        method: "GET",
      }),
    }),
    createPaymentIntent: builder.mutation({
      query: (params) => ({
        url: "user/createPaymentInstant",
        method: "POST",
        body: params,
      }),
    }),
    createJobPayment: builder.mutation({
      query: (params) => ({
        url: "user/createPayment",
        method: "POST",
        body: params,
      }),
    }),
    subscribeUserToPlan: builder.mutation({
      query: (params) => ({
        url: "admin/subscribeUserToPlan",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ['Subscription'],
    }),
    redeemCoupan: builder.mutation({
      query: (params) => ({
        url: "user/redeemCoupon",
        method: "POST",
        body: params,
      }),
    }),
    redeemSummary: builder.mutation({
      query: (params) => ({
        url: "user/getRedeemSummary",
        method: "POST",
        body: params,
      }),
    }),
    requestRedeem: builder.mutation({
      query: (params) => ({
        url: "user/requestRedeem",
        method: "POST",
        body: params,
      }),
    }),
    userAcceptRequestRedeem: builder.mutation({
      query: (params) => ({
        url: "user/acceptRedeemRequest",
        method: "POST",
        body: params,
      }),
    }),
    getCustomerPaymentMethods: builder.query({
      query: () => ({
        url: "user/getCustomerPaymentMethods",
        method: "POST",
        body: {},
      }),
    }),
    deletePaymentMethod: builder.mutation({
      query: (params) => ({
        url: "user/deletePaymentMethod",
        method: "POST",
        body: params,
      }),
    }),
    setDefaultPaymentMethod: builder.mutation({
      query: (params) => ({
        url: "user/setDefaultPaymentMethod",
        method: "POST",
        body: params,
      }),
    }),
    cancelUserSubscription: builder.mutation({
      query: (params) => ({
        url: "admin/cancelUserSubscription",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ['Subscription'],
    }),
    connectStripe: builder.mutation({
      query: (params) => ({
        url: "user/stripeVarificationLink",
        method: "POST",
        body: {},
      }),
    }),
    getPayoutHistory: builder.mutation({
      query: (params) => ({
        url: "admin/getPayoutHistory",
        method: "POST",
        body: params,
      }),
    }),
    getPayoutSummary: builder.mutation({
      query: (params) => ({
        url: "admin/getPayoutSummary",
        method: "POST",
        body: params,
      }),
    }),
    getPayPayout: builder.mutation({
      query: (params) => ({
        url: "admin/adminPayPayout",
        method: "POST",
        body: params,
      }),
    }),
    getAdminCampaignSummaryForReviewer: builder.mutation({
      query: (params) => ({
        url: "user/getAdminCampaignSummaryForReviewer",
        method: "POST",
        body: {},
      }),
      providesTags: ['CampaignForReviewer'],
    }),
    requestAdminRedeem: builder.mutation({
      query: (params) => ({
        url: "user/requestAdminRedeem",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ['CampaignForReviewer'],
    }),

    // Subscription Endpoints
    getCurrentSubscription: builder.query({
      query: () => ({
        url: "admin/subscriptions/current",
        method: "GET",
      }),
      providesTags: ['Subscription'],
    }),
    cancelSubscription: builder.mutation({
      query: (params) => ({
        url: "admin/subscriptions/cancel",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ['Subscription'],
    }),
    reactivateSubscription: builder.mutation({
      query: () => ({
        url: "admin/subscriptions/reactivate",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ['Subscription'],
    }),
    getSubscriptionHistory: builder.query({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: `admin/subscriptions/history?page=${page}&limit=${limit}`,
        method: "GET",
      }),
      providesTags: ['Subscription'],
    }),
    getAdminSubscriptionPlans: builder.query({
      query: () => ({
        url: "admin/getSubscriptionPlans",
        method: "GET",
      }),
      providesTags: ['Subscription'],
    }),
    getAllSubscriptions: builder.query({
      query: ({ page = 1, limit = 10, status = 'all', planId = 'all', search = '', startDate = '', endDate = '' } = {}) => {
        const params = new URLSearchParams({
          page,
          limit,
          status,
          planId,
        });

        if (search) params.append("search", search);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        return {
          url: `admin/getAllSubscriptions?${params}`,
          method: "GET",
        };
      },
      providesTags: ['Subscription'],
    }),
    getSubscriptionDetails: builder.query({
      query: (subscriptionId) => ({
        url: `admin/getSubscriptionDetails/${subscriptionId}`,
        method: "GET",
      }),
      providesTags: (result, error, subscriptionId) => [{ type: 'Subscription', id: subscriptionId }],
    }),
    getUserSubscriptionHistory: builder.query({
      query: (userId) => ({
        url: `admin/getUserSubscriptionHistory/${userId}`,
        method: "GET",
      }),
      providesTags: (result, error, userId) => [{ type: 'Subscription', id: userId }],
    }),

    // Transaction Endpoints
    getMyTransactions: builder.query({
      query: ({ page = 1, limit = 10, status = 'all', type = 'all', startDate = '', endDate = '', userId = 'all' } = {}) => {
        const params = new URLSearchParams({
          page,
          limit,
          status,
          type,
        });

        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (userId !== "all") params.append("userId", userId);

        return {
          url: `user/getMyTransactions?${params}`,
          method: "GET",
        };
      },
      providesTags: ['Transactions'],
    }),
    getTransactionDetails: builder.query({
      query: (transactionId) => ({
        url: `user/getTransactionDetails/${transactionId}`,
        method: "GET",
      }),
      providesTags: (result, error, transactionId) => [{ type: 'Transactions', id: transactionId }],
    }),
    downloadInvoice: builder.query({
      query: (transactionId) => ({
        url: `user/downloadInvoice/${transactionId}/invoice`,
        method: "GET",
      }),
    }),
    getAllEmployers: builder.query({
      query: () => ({
        url: "admin/getAllEmployers",
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useDeleteNotificationMutation,
  useReadNotificationMutation,
  useReadAllNotificationMutation,
  useGetDashboardQuery,
  useUpdateProfileMutation,
  useChangeProfilePasswordMutation,
  useGetUserDataQuery,
  useCreatePaymentIntentMutation,
  useCreateJobPaymentMutation,
  useSubscribeUserToPlanMutation,
  useRedeemCoupanMutation,
  useRedeemSummaryMutation,
  useRequestRedeemMutation,
  useUserAcceptRequestRedeemMutation,
  useGetCustomerPaymentMethodsQuery,
  useDeletePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useCancelUserSubscriptionMutation,
  useConnectStripeMutation,
  useGetAdminCampaignSummaryForReviewerMutation,
  useRequestAdminRedeemMutation,
  useGetPayoutHistoryMutation,
  useGetPayoutSummaryMutation,
  useGetPayPayoutMutation,
  useGetEmployerPostedJobsQuery,
  useGetEmployeeAssignedJobsQuery,

  // Subscription Hooks
  useGetCurrentSubscriptionQuery,
  useCancelSubscriptionMutation,
  useReactivateSubscriptionMutation,
  useGetSubscriptionHistoryQuery,
  useGetAdminSubscriptionPlansQuery,
  useGetAllSubscriptionsQuery,
  useLazyGetAllSubscriptionsQuery,
  useGetSubscriptionDetailsQuery,
  useLazyGetSubscriptionDetailsQuery,
  useGetUserSubscriptionHistoryQuery,
  useLazyGetUserSubscriptionHistoryQuery,

  // Transaction Hooks
  useGetMyTransactionsQuery,
  useLazyGetMyTransactionsQuery,
  useGetTransactionDetailsQuery,
  useLazyGetTransactionDetailsQuery,
  useLazyDownloadInvoiceQuery,
  useGetAllEmployersQuery,
} = CommonAPI;